import React from 'react';
import type { FuelPlan } from '../types';

// Built from context-chicago/fuelling-research.md. Same low-FODMAP protocol and 70 kg targets as London 2026,
// re-timed for a 07:35 Wave 1 gun (Saturday runs ~90 min earlier than London so bedtime still gives 7 h sleep).
export const chicagoFuelling: FuelPlan = {
  referenceWeightKg: 70,
  waves: [
    { id: 'w1', label: 'Wave 1', time: '07:35', detail: 'corrals close 07:20' },
    { id: 'w2', label: 'Wave 2', time: '08:00', detail: 'corrals close 07:45' },
    { id: 'w3', label: 'Wave 3', time: '08:35', detail: 'corrals close 08:10' },
  ],
  defaultWaveId: 'w1',
  ticker: [
    { label: 'Thu 8 · ramp', value: '555', unit: 'g CHO', sub: '8 g/kg · 2,830 kcal' },
    { label: 'Fri 9 · load', value: '785', unit: 'g CHO', sub: '11 g/kg · 3,790 kcal' },
    { label: 'Sat 10 · peak', value: '810', unit: 'g CHO', sub: '11.5 g/kg · 3,850 kcal · fibre ≤10' },
    { label: 'Sun 11 AM', value: '175', unit: 'g + gel', sub: 'pre-race · 2.5 g/kg' },
  ],
  calendarDates: ['Sun 4', 'Mon 5', 'Tue 6', 'Wed 7', 'Thu 8', 'Fri 9', 'Sat 10', 'Sun 11'],
  calendarRows: [
    {
      name: 'Nomio · 1 shot ×2/day',
      sub: '3-day preload · AM + PM, 12 h apart · start Thu 8 AM in Chicago · 6 shots',
      cells: [
        { label: '·', tone: 'dot' },
        { label: '·', tone: 'dot' },
        { label: '·', tone: 'dot' },
        { label: '·', tone: 'dot' },
        { label: 'START', tone: 'on' },
        { label: 'AM+PM', tone: 'on' },
        { label: 'AM+PM', tone: 'on' },
        { label: '·', tone: 'dot' },
      ],
    },
    {
      name: 'Beetroot · 70 mL shot',
      sub: 'Beet It Sport Nitrate 400 · ~6.4 mmol · start Mon 5 in Melbourne',
      cells: [
        { label: '·', tone: 'dot' },
        { label: 'START', tone: 'on' },
        { label: 'AM', tone: 'on' },
        { label: '✈ 1 shot', tone: 'am' },
        { label: 'AM', tone: 'on' },
        { label: 'AM', tone: 'on' },
        { label: 'AM', tone: 'on' },
        { label: 'T-3:00', tone: 'am' },
      ],
    },
  ],
  calendarSub:
    'Beetroot starts at home on Mon 5; Nomio is a 3-day preload from Thu 8 in Chicago. You already have enough of both — pack them in checked luggage (one or two shots under 100 mL can go in the carry-on bag).',
  days: [
    {
      day: 'Thursday 8',
      tag: 'T-3 · ramp',
      kcal: '2,830 kcal',
      meals: [
        { time: '07:00', description: '3 slices Canyon Country White · jam · firm banana · black coffee (before the Green Line)', cho: 130 },
        { time: '10:00', description: '2 rice cakes · jam · kiwi · 250 mL Gatorade (before the Art Institute)', cho: 85 },
        { time: '13:15', description: 'Packed: 2 c jasmine rice · 100 g chicken · soy · orange (Millennium Park, before the cruise)', cho: 100 },
        { time: '16:45', description: 'Quartino: plain pasta or risotto, no garlic/onion · 2 rice bars after', cho: 105 },
        { time: '19:30', description: 'At the loft: 3 slices GF toast · maple · jam · firm banana · 500 mL Gatorade', cho: 135 },
      ],
      totals: { cho: '555 g', protein: '82 g', fat: '24 g', fluid: '3.0 L' },
      note: "Ramp day, not full load — don't stress exact grams at dinner. Order plain: pasta or risotto with olive oil and parmesan, sauce on the side, and say 'no garlic, no onion' (US sauces almost always have both).",
    },
    {
      day: 'Friday 9',
      tag: 'T-2 · full load',
      kcal: '3,790 kcal',
      meals: [
        {
          time: '07:30',
          description: '4 slices GF toast · jam · maple · firm banana · 250 mL Lactaid · 125 mL OJ',
          cho: 165,
        },
        { time: '10:00', description: '3 slices GF toast · jam · maple · 500 mL Gatorade (packed — eat at the Expo)', cho: 130 },
        { time: '12:30', description: 'Kai Sushi: 3 simple maki rolls (salmon, tuna, cucumber) + side of plain rice · soy · no tempura or mayo', cho: 150 },
        { time: '15:00', description: '3 rice cakes · maple · firm banana · 500 mL Gatorade', cho: 95 },
        { time: '16:45', description: 'At the loft: 200 g GF pasta · plain passata · 100 g chicken · 2 GF toast w/ maple', cho: 155 },
        { time: '19:30', description: 'At the Bulls: 2 Rice Krispies Treats · firm banana · 500 mL Gatorade', cho: 90 },
      ],
      totals: { cho: '785 g', protein: '89 g', fat: '26 g', fluid: '3.5 L' },
      note: 'Expo at 9, sushi lunch at Kai, then Bulls v Grizzlies at 7 pm — so the big dinner moves to 16:45 at the loft and the 90 g snack goes to the game. If Kai isn’t open, eat 2.5 c rice at the loft at 14:00 instead. If the United Center won’t let snacks in, eat them before the car at 18:00.',
    },
    {
      day: 'Saturday 10',
      tag: 'T-1 · leanest · low fibre · in bed 20:30',
      kcal: '3,850 kcal · last bite 18:45',
      meals: [
        {
          time: '06:15',
          description: '4 slices GF toast · jam · maple · firm banana · 250 mL Lactaid · salt · 250 mL Gatorade (before the 5K)',
          cho: 190,
        },
        { time: '10:00', description: 'Packed: 2 slices GF toast · jam · maple · 500 mL Gatorade (after the lululemon shakeout)', cho: 115 },
        { time: '12:00', description: '2.5 c jasmine rice · 100 g chicken · salt · soy · strawberries', cho: 145 },
        { time: '14:30', description: '3 rice cakes · maple · firm banana · 500 mL Gatorade', cho: 95 },
        { time: '16:30', description: '2.5 c rice · 1 can Wild Planet tuna · salt · 3 GF toast w/ jam', cho: 175 },
        { time: '18:30', description: '3 rice bars · firm banana · 250 mL Gatorade · final Nomio PM shot', cho: 90 },
      ],
      totals: { cho: '810 g', protein: '84 g', fat: '19 g', fluid: '3.5 L', sodium: '4.0 g Na' },
      note: 'Early start for the Chicago 5K and the lululemon shakeout — breakfast at 06:15, the 10:00 snack travels with you, then every meal from lunch is at the loft. Shifted ~90 min earlier than London so a 03:45 alarm (Wave 1) still leaves 7 h in bed. On Wave 2/3, slide every meal later by the same gap.',
    },
  ],
  daysSub:
    'Meal-by-meal for the three build days — low-FODMAP, low fibre, lactose permitted, US brands from the shopping list. Written for Wave 1.',
  morningTitle: 'Race morning · Sunday 11',
  morningSub:
    'Kit out the night before · bib · 8 PF 30s (caf ones marked) · beetroot shot · PF Carb Mix pre-measured in 2-scoop bags · throwaway layer · clear gear-check bag with ID, phone and a dry top. Car booked for the drive in from Woodlawn.',
  morning: [
    { offset: -230, what: 'Wake · 300 mL water + pinch salt', note: 'Rehydrate' },
    {
      offset: -210,
      what: 'Pre-race: 2 GF toast · 40 mL maple · jam · banana · PF Carb Mix 2 scoops in 300–500 mL water (30 g CHO, 500 mg Na) · strong coffee (~100 mg caf)',
      note: '175 g CHO',
      variant: 'key',
    },
    { offset: -180, what: 'Beetroot shot 70 mL', note: '~6.4 mmol NO₃', variant: 'key' },
    { offset: -175, until: -135, what: 'Feet up at the loft · sip 400 mL water + 500 mg Na tab', note: '500 mg Na' },
    { offset: -135, what: 'Stop bulk fluid · bathroom' },
    {
      offset: -125,
      what: 'Car leaves the loft · PF Carb Mix 2 scoops in a 500 mL bottle (30 g CHO), sip ≤150 mL on the way · drop-off west of Michigan Ave, south of Ida B. Wells Dr',
      note: '~25 min drive · PV expand',
      variant: 'key',
    },
    { offset: -95, what: 'Walk to the gate on your bib (Jackson / Van Buren / Ida B. Wells / Harrison) · gear check · security (allow 15–30 min) · bathroom queue straight away' },
    { offset: -45, what: 'Warm-up: 8 min jog in Grant Park · drills · 3–4 strides', note: 'Sip 100 mL' },
    { offset: -28, what: 'Into corral — be there ~20 min before it closes · drop throwaway layer at the start' },
    { offset: -14, what: 'Caf PF 30 gel + 100 mL water', note: '+100 mg caf', variant: 'key' },
    { offset: 0, what: 'GUN · Columbus Dr at Monroe', variant: 'gun' },
  ],
  raceRules: [
    {
      label: 'In-race',
      body: (
        <>
          Same cadence as London: first gel at 15 min, then every 25 min · target <strong>90 g CHO/h</strong> · water at
          every aid station (water is the <strong>last</strong> table; Gatorade comes first) · 500–600 mg Na/h · 600–800
          mL/h.
        </>
      ),
    },
    {
      label: 'Gel plan',
      body: (
        <>
          1 caf pre-start + 6 in-race: <strong>non-caf × 3 · caf · non-caf × 2</strong> · carry 8 (one spare). Course
          Maurten Gel 100s at miles 12.4 and 18.1 are an emergency backup only — not trialled.
        </>
      ),
    },
  ],
  panels: [
    {
      kind: 'list',
      icon: 'pill',
      title: 'Race-day stack',
      tag: 'supplements',
      rows: [
        { key: 'T-3:30', text: 'Pre-race meal + strong coffee (~100 mg caf)' },
        { key: 'T-3:00', text: 'Beetroot shot 70 mL' },
        { key: 'T-2:05', text: 'PF Carb Mix 2 scoops in 500 mL (car to the start)' },
        { key: 'T-14', text: 'Caf PF 30 gel + 100 mL water (+100 mg)' },
        { key: 'Gel 4', text: 'Caf PF 30 gel, ~90 min in (+100 mg)' },
        { key: 'Total caf', text: '~300 mg (4.3 mg/kg) · coffee + 2 caf gels' },
      ],
    },
    {
      kind: 'list',
      icon: 'droplet',
      title: 'Hydration & sodium',
      tag: 'targets',
      rows: [
        { key: 'Thu', text: '3.0 L · 3.5 g Na · salt meals' },
        { key: 'Fri', text: '3.5 L · 4.0 g Na · 2× 500 mg electrolyte drinks' },
        { key: 'Sat', text: '3.5 L · taper after 18:00 · 4.0 g Na' },
        { key: 'Pre-race', text: '~1.5 g Na in ~1.2 L fluid (PF Carb Mix ×2 + Na tab)' },
        { key: 'In-race', text: '500–600 mg Na/h · 600–800 mL/h · more if the start is above 15 °C' },
      ],
    },
    {
      kind: 'list',
      icon: 'flag',
      title: 'Chicago course fuel',
      tag: 'on course',
      wide: true,
      rows: [
        { key: 'Aid stations', text: '20, every 1–2 miles · medical, toilets, Gatorade tables, then water' },
        { key: 'Drink', text: 'Gatorade Endurance lemon-lime · ~23 g CHO + 310 mg Na per 12 oz · sips only (untrialled)' },
        { key: 'Gels', text: 'Maurten Gel 100 (25 g, caf & non-caf) at mile 12.4 (AS 9) and mile 18.1 (AS 14)' },
        { key: 'Bananas', text: 'Aid stations 16–18, miles 20.2–23' },
        { key: 'Refill', text: 'Water refill stations at miles 5.7, 10.1, 15.7, 20.3 — refill a PF Carb Mix soft flask from pre-measured 2-scoop bags' },
        { key: 'Course', text: 'Flat — the only climb is the Roosevelt Rd bridge ~500 m from the finish · miles 1–2 reportedly via Dearborn in 2026' },
        { key: 'Weather', text: 'Start temps 7–15 °C most years (20 °C in 2021) · recheck the forecast from Thu 8' },
      ],
    },
    {
      kind: 'text',
      icon: 'ban',
      title: 'Do not eat this week',
      tag: 'avoid',
      wide: true,
      text: 'Onion · garlic (use Fody garlic-infused oil) · wheat pasta · honey · agave · HFCS · apple · pear · mango · watermelon · dried fruit >1 tbsp · legumes · cauliflower · mushrooms · sugar-free anything · inulin / chicory root · polyols. US label traps: Canyon Mountain White (agave), Schär US Artisan White (honey + psyllium), rotisserie chicken and seasoned strips (garlic/onion powder), "pancake syrup" (corn syrup), StarKist tuna (vegetable broth).',
    },
    {
      kind: 'grid',
      icon: 'alert',
      title: 'If things go sideways',
      tag: 'contingency',
      wide: true,
      grid: [
        {
          title: 'No appetite at breakfast',
          text: 'Minimum 140 g CHO (2 g/kg): 2 × 500 mL PF Carb Mix (60 g) + 2 PF 30 gels (60 g) + a banana. Never skip.',
        },
        {
          title: 'GI niggle in-race',
          text: 'Drop to 60 g/h and stick to PF 30s and water. Skip Gatorade and the course Maurten.',
        },
        {
          title: 'Start above 15 °C',
          text: 'Add a Na tab to the pre-race bottle, take water at every station, pour one cup over your head.',
        },
        {
          title: 'Bag delayed or gels short',
          text: 'Running Warehouse ships PF 30 overnight; PF Carb Mix ships from precisionhydration.com. Confirm the loft can take deliveries.',
        },
      ],
    },
  ],
  footerTitle: 'Race-week fuelling',
  footerSub: 'Expect to weigh 1.5–2.0 kg more by race morning — that’s stored glycogen and water.',
};
